import { useEffect, useMemo, useState } from 'react';
import { rowToHttpRequest } from './useHistoryDatasource';
import { useProxyStore, type HttpRequest } from '../store/proxy-store';

type MergeRecentRequestsArgs = {
  historyRequests: HttpRequest[];
  liveRequests: Record<string, HttpRequest>;
  limit: number;
};

export function mergeRecentRequests({
  historyRequests,
  liveRequests,
  limit,
}: MergeRecentRequestsArgs): HttpRequest[] {
  const byId = new Map<string, HttpRequest>();

  for (const request of historyRequests) {
    byId.set(request.id, request);
  }

  for (const request of Object.values(liveRequests)) {
    byId.set(request.id, request);
  }

  return [...byId.values()]
    .sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime())
    .slice(0, limit);
}

export type UseRecentHistoryRequestsArgs = {
  targetId?: string | null;
  targetType?: string | null;
  limit?: number;
};

export function useRecentHistoryRequests({
  targetId: targetIdProp,
  targetType: targetTypeProp,
  limit = 50,
}: UseRecentHistoryRequestsArgs): HttpRequest[] {
  const liveRequests = useProxyStore((state) => state.requests);
  const historyVersion = useProxyStore((state) => state.historyVersion);
  const storeTargetId = useProxyStore((state) => state.currentTargetId);
  const storeTargetType = useProxyStore((state) => state.currentTargetType);
  const [historyRequests, setHistoryRequests] = useState<HttpRequest[]>([]);

  useEffect(() => {
    const targetId = targetIdProp || storeTargetId || 'standalone';
    const targetType = targetTypeProp || storeTargetType || 'WEB_APPLICATION';

    if (
      typeof window === 'undefined' ||
      !window.electronAPI?.history?.getRows
    ) {
      setHistoryRequests([]);
      return;
    }

    let cancelled = false;

    window.electronAPI.history
      .getRows({
        targetId,
        targetType,
        startRow: 0,
        endRow: limit,
        sortModel: [{ colId: 'timestamp', sort: 'desc' }],
        filters: {},
      })
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (result.success && result.data) {
          setHistoryRequests(result.data.rows.map(rowToHttpRequest));
          return;
        }

        setHistoryRequests([]);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        console.warn('Failed to load recent history requests', error);
        setHistoryRequests([]);
      });

    return () => {
      cancelled = true;
    };
  }, [
    targetIdProp,
    targetTypeProp,
    storeTargetId,
    storeTargetType,
    limit,
    historyVersion,
  ]);

  return useMemo(
    () =>
      mergeRecentRequests({
        historyRequests,
        liveRequests,
        limit,
      }),
    [historyRequests, liveRequests, limit],
  );
}
